/* eslint-disable @typescript-eslint/ban-ts-comment */
import { PositionedImage } from './image';
import { PositionedText } from './positioned-text';
import { Certificate } from './types';

const createCanvas = (width: number, height: number) => {
  // @ts-ignore
  return new OffscreenCanvas(width, height);
};

export class CanvasRenderer {
  public finalPath: string | undefined;
  public ready = false;
  protected canvas: HTMLCanvasElement | undefined;
  protected ctx: CanvasRenderingContext2D | null = null;
  public userName: PositionedText;
  public certificateName: PositionedText;
  public dateString: PositionedText;
  public byline: PositionedText;
  public credits?: PositionedText;
  public categories?: PositionedText;
  protected designId: string;
  protected backgroundFrame: ImageBitmap | undefined;
  public images: PositionedImage[] = [];
  protected cursor = 0;

  // @ts-ignore
  protected offscreenCanvas: OffscreenCanvas | undefined;

  // @ts-ignore
  protected offscreenCtx: OffscreenCanvasRenderingContext2D | undefined;

  constructor(
    protected certificateDesign: Certificate,
    protected height = 1080,
    protected width = 1920
  ) {
    this.designId = certificateDesign.settings.id;
    this.userName = PositionedText.usernameFromCerficicateDesign(certificateDesign);
    this.byline = PositionedText.bylineFromCerficicateDesign(certificateDesign);
    this.certificateName = PositionedText.certificateNameFromCerficicateDesign(certificateDesign);
    this.dateString = PositionedText.dateStringFromCerficicateDesign(certificateDesign);
    this.credits = PositionedText.creditsFromCerficicateDesign(certificateDesign);
    this.categories = PositionedText.categoriesFromCerficicateDesign(certificateDesign);
  }

  updateCertificateDesign = async (certificateDesign: Certificate) => {
    this.certificateDesign = certificateDesign;
    this.userName = PositionedText.usernameFromCerficicateDesign(certificateDesign);
    this.byline = PositionedText.bylineFromCerficicateDesign(certificateDesign);
    this.certificateName = PositionedText.certificateNameFromCerficicateDesign(certificateDesign);
    this.dateString = PositionedText.dateStringFromCerficicateDesign(certificateDesign);
    this.credits = PositionedText.creditsFromCerficicateDesign(certificateDesign);
    this.categories = PositionedText.categoriesFromCerficicateDesign(certificateDesign);
    await this.init(this.canvas as HTMLCanvasElement);
  };

  drawImages() {
    if (this.offscreenCtx) {
      this.offscreenCtx.clearRect(0, 0, this.width, this.height);
      this.images.forEach((image) => {
        if (image.bitmap) {
          // @ts-ignore
          this.offscreenCtx.drawImage(image.bitmap, image.x, image.y, image.width, image.height);
        }
      });
    }
  }

  init = async (canvas: HTMLCanvasElement) => {
    const backgroundImage = new PositionedImage(
      this.certificateDesign.settings.backgroundImage,
      this.height,
      this.width
    );
    await backgroundImage.load();
    const images = [backgroundImage];
    const icon = this.certificateDesign.settings.icon
      ? new PositionedImage(
          this.certificateDesign.settings.icon,
          this.certificateDesign.settings.positions.icon?.height,
          this.certificateDesign.settings.positions.icon?.width,
          this.certificateDesign.settings.positions.icon?.x,
          this.certificateDesign.settings.positions.icon?.y
        )
      : undefined;

    if (icon) {
      await icon.load();
      images.push(icon);
    }

    this.images = images;

    this.offscreenCanvas = createCanvas(this.width, this.height);

    // @ts-ignore
    this.offscreenCtx = this.offscreenCanvas.getContext('2d', { alpha: false }) as OffscreenCanvasRenderingContext2D;

    this.drawImages();

    this.canvas = canvas;

    this.ctx = this.canvas.getContext('2d', { alpha: false }) as CanvasRenderingContext2D;

    // @ts-ignore
    this.backgroundFrame = this.offscreenCanvas.transferToImageBitmap();

    // @ts-ignore
    this.ctx.drawImage(this.backgroundFrame, 0, 0);

    this.draw();
    this.ready = true;
  };

  updateIcon = async (_icon: string) => {
    const icon = new PositionedImage(
      _icon,
      this.certificateDesign.settings.positions.icon?.height,
      this.certificateDesign.settings.positions.icon?.width,
      this.certificateDesign.settings.positions.icon?.x,
      this.certificateDesign.settings.positions.icon?.y
    );

    await icon.load();

    this.images[1] = icon;
    this.drawImages();

    // @ts-ignore
    this.backgroundFrame = this.offscreenCanvas.transferToImageBitmap();

    // @ts-ignore
    this.draw();
  };

  draw() {
    if (this.ready && this.ctx && this.backgroundFrame) {
      this.ctx.clearRect(0, 0, this.width, this.height);
      this.ctx.drawImage(this.backgroundFrame, 0, 0);
      this.writeText(this.userName);
      this.writeText(this.byline);
      this.writeText(this.certificateName);
      this.writeText(this.dateString);
      if (this.credits) {
        this.writeText(this.credits);
      }
      if (this.categories) {
        this.writeText(this.categories);
      }
      this.cursor = 0;
    }
  }

  writeText(text: PositionedText) {
    if (!this.ctx) {
      throw new Error('Canvas context not initialized');
    }

    this.ctx.font = text.font;
    this.ctx.fillStyle = text.color;
    this.ctx.textAlign = text.align;

    // @ts-ignore
    this.ctx.letterSpacing = text.letterSpacing || '0.01px'; // must have a fallback value or some fonts will not render properly

    // if the line above has pushed the cursor down, we need to adjust the y position
    // to prevent overlapping text
    const fontSize = text.getFontSize();
    const targetY = Math.max(text.y, this.cursor + fontSize);
    const wrapMaxWidthPercentage = 0.6;

    // if the text is wider than the alotted space, we need to wrap it to two lines
    if (this.ctx.measureText(text.text).width > this.width * wrapMaxWidthPercentage) {
      const len = text.text.split(' ').length;

      // split the text into two roughly equal halves
      const firstHalf = text.text
        .split(' ')
        .slice(0, len * wrapMaxWidthPercentage)
        .join(' ');
      const secondHalf = text.text
        .split(' ')
        .slice(len * wrapMaxWidthPercentage)
        .join(' ');

      // write the first half at the target y position
      this.ctx.fillText(firstHalf, text.x, targetY, this.width * wrapMaxWidthPercentage);

      // adjust the y position for the second half plus 120% of the font size for leading
      const adjustedY = targetY + fontSize * 1.2;

      // write the second half at the adjusted y position
      this.ctx.fillText(secondHalf, text.x, adjustedY, this.width * wrapMaxWidthPercentage);

      // set the cursor to the adjusted y position
      this.cursor = adjustedY + fontSize * 0.5;
    } else {
      this.ctx.fillText(text.text, text.x, targetY);
      this.cursor = targetY + fontSize * 0.5;
    }
  }

  exportToPdf = async (filename: string) => {
    try {
      if (!this.canvas) {
        return;
      }

      const jsPDF = (await import('jspdf')).default;
      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'px',
        format: [this.width, this.height],
      });
      pdf.addImage(this.canvas, 'JPEG', 0, 0, this.width, this.height);
      pdf.save(filename);
    } catch (e) {
      console.error(e);
      throw e;
    }
  };
}
